/**
 * Company Management Sidebar
 *
 * Dedicated sidebar for the "Company Mode" layout.
 * Shows company/store info, store selector, and navigation
 * organized by company-level and store-level sections.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useStoreManagement } from '@/contexts/StoreManagementContext';
import { Badge } from '@/components/ui';
import { STORE_STATUS_LABELS, STORE_STATUS_COLORS } from '@/types/store.types';

// ============================================================================
// NAV ITEMS
// ============================================================================

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  section?: 'company' | 'store';
}

const COMPANY_NAV_ITEMS: NavItem[] = [
  {
    label: 'Catálogo',
    path: 'catalog',
    section: 'company',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
  },
  {
    label: 'Promociones',
    path: 'promotions',
    section: 'company',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5.5 8.5 9 12l-3.5 3.5L2 12l3.5-3.5Z" />
        <path d="m12 2 3.5 3.5L12 9 8.5 5.5 12 2Z" />
        <path d="M18.5 8.5 22 12l-3.5 3.5L15 12l3.5-3.5Z" />
        <path d="m12 15 3.5 3.5L12 22l-3.5-3.5L12 15Z" />
      </svg>
    ),
  },
  {
    label: 'Fidelización',
    path: 'loyalty',
    section: 'company',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
      </svg>
    ),
  },
];

const STORE_NAV_ITEMS: NavItem[] = [
  {
    label: 'Menú',
    path: 'menu',
    section: 'store',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    ),
  },
  {
    label: 'Horarios',
    path: 'schedule',
    section: 'store',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
  {
    label: 'Configuración',
    path: 'settings',
    section: 'store',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
];

// ============================================================================
// ICONS
// ============================================================================

const ArrowLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ============================================================================
// SIDEBAR COMPONENT
// ============================================================================

interface CompanyManagementSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CompanyManagementSidebar({ isOpen, onClose }: CompanyManagementSidebarProps) {
  const { store, company, companyId } = useStoreManagement();
  const location = useLocation();
  const navigate = useNavigate();

  // Derive active path from URL
  const activePath = location.pathname.split('/manage/')[1]?.split('/')[0] || 'menu';

  const basePath = `/my-companies/${companyId}/stores/${store?.id}/manage`;

  const renderNavItem = (item: NavItem) => {
    const isActive = activePath === item.path;
    return (
      <li key={item.path}>
        <Link
          to={`${basePath}/${item.path}`}
          onClick={onClose}
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
            isActive
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          )}
        >
          {item.icon}
          {item.label}
        </Link>
      </li>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-full w-64 flex-col border-r bg-background transition-transform lg:static lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Company header */}
        <div className="border-b px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Modo Empresa</p>
            <button
              onClick={onClose}
              aria-label="Cerrar menú"
              className="cursor-pointer rounded-md p-1.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Company logo + name */}
          <div className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2.5">
            {company?.logoUrl ? (
              <img
                src={company.logoUrl}
                alt={company.name}
                className="h-9 w-9 flex-shrink-0 rounded-lg object-cover"
              />
            ) : (
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold text-sm">
                {company?.name?.charAt(0)?.toUpperCase() || 'E'}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{company?.name || 'Empresa'}</p>
              <p className="text-xs text-muted-foreground truncate">{store?.name || 'Sucursal'}</p>
            </div>
          </div>
          {store?.status && (
            <div className="mt-2">
              <Badge
                variant={STORE_STATUS_COLORS[store.status] || 'default'}
                size="sm"
              >
                {STORE_STATUS_LABELS[store.status] || store.status}
              </Badge>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Company-level section */}
          <div>
            <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Empresa
            </h3>
            <ul className="space-y-1">
              {COMPANY_NAV_ITEMS.map(renderNavItem)}
            </ul>
          </div>

          {/* Store-level section */}
          <div>
            <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Sucursal
            </h3>
            <ul className="space-y-1">
              {STORE_NAV_ITEMS.map(renderNavItem)}
            </ul>
          </div>
        </nav>

        {/* Footer — back to admin */}
        <div className="border-t p-4">
          <button
            onClick={() => navigate('/my-companies')}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
          >
            <ArrowLeftIcon />
            Volver al Panel
          </button>
        </div>
      </aside>
    </>
  );
}

export default CompanyManagementSidebar;
